'use client';

/**
 * @fileoverview Client-only mount for `NotificationCenter` on the app consumer
 * shell (WEB-635).
 *
 * `AppShellLayout` stays an RSC — this sibling resolves the session user id
 * and mounts the panel host once. Open state is owned by `useNotificationPanel`
 * (menu row activation); there is no bar bell trigger.
 */

import * as React from 'react';
import type { CityCode } from '@loci/city-editions';
import { useGetCurrentUserQuery } from '@loci/api';

import { NotificationCenter } from '@/components/organisms/NotificationCenter';
import { useNotificationPanel } from '@/hooks/useNotificationPanel';
import { useSessionHint } from '@/lib/auth';

interface CurrentUserShape {
  readonly id?: string | null;
}

function pickUserId(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const record = data as Record<string, unknown>;
  const candidate =
    (record.user as CurrentUserShape | undefined) ??
    (record.data as CurrentUserShape | undefined) ??
    (record as CurrentUserShape);
  const id = candidate?.id;
  return typeof id === 'string' && id.length > 0 ? id : null;
}

interface NotificationCenterMountProps {
  readonly cityCode: CityCode;
}

export function NotificationCenterMount({
  cityCode,
}: NotificationCenterMountProps): React.ReactElement | null {
  const hasSession = useSessionHint();
  const { data } = useGetCurrentUserQuery(undefined, { skip: !hasSession });
  const userId = hasSession ? pickUserId(data) : null;
  const { closePanel } = useNotificationPanel();

  // When the host unmounts NotificationCenter (signed out / user id gone),
  // clear the module open bit so a later remount cannot auto-open the panel.
  React.useEffect(() => {
    if (!userId) closePanel();
  }, [userId, closePanel]);

  if (!userId) return null;
  return <NotificationCenter userId={userId} cityCode={cityCode} />;
}
